// Map-owned debris (§11.4): porcelain chips, cake crumbs, bubble droplets and regrow sprinkles.
// One InstancedMesh of low-poly chunks with per-instance color, simulated on the CPU with gravity,
// drag and a bounce on the arena floor. Capacity follows the tier (§12.9 particle budgets; VFX
// owns the rest of the budget).
import { Color, DynamicDrawUsage, IcosahedronGeometry, InstancedMesh, Matrix4, Quaternion, Vector3, type ColorRepresentation, type Material } from 'three';
import type { QualityTier } from '../../Renderer';

export const PARTICLE_CAPACITY: Readonly<Record<QualityTier, number>> = { high: 220, medium: 160, low: 96, potato: 48 };

export interface Burst {
  /** three.js position */
  x: number; y: number; z: number;
  count: number;
  color: ColorRepresentation | readonly ColorRepresentation[];
  speed: number;
  up: number;
  size: number;
  life: number;
  /** Spread radius of the spawn area. */
  spread?: number;
  gravity?: number;
  /** Elongation (sprinkles). */
  stretch?: number;
}

export interface MapParticles {
  readonly mesh: InstancedMesh;
  burst(b: Burst): void;
  /** Ground height lookup (three.js y or null for none) so debris lands on the arena. */
  setFloor(fn: (x: number, z: number) => number | null): void;
  update(dt: number): void;
  dispose(): void;
}

export function createMapParticles(tier: QualityTier, material: Material): MapParticles {
  const cap = PARTICLE_CAPACITY[tier];
  const geo = new IcosahedronGeometry(1, 0);
  const mesh = new InstancedMesh(geo, material, cap);
  mesh.instanceMatrix.setUsage(DynamicDrawUsage);
  mesh.count = 0;
  mesh.frustumCulled = false;
  mesh.castShadow = false;
  mesh.name = 'map-particles';
  const px = new Float32Array(cap * 3);
  const pv = new Float32Array(cap * 3);
  const life = new Float32Array(cap);
  const maxLife = new Float32Array(cap);
  const size = new Float32Array(cap);
  const stretch = new Float32Array(cap);
  const grav = new Float32Array(cap);
  const spin = new Float32Array(cap * 3);
  const colors: Color[] = Array.from({ length: cap }, () => new Color());
  let alive = 0;
  let floor: (x: number, z: number) => number | null = () => null;
  const m = new Matrix4();
  const q = new Quaternion();
  const v = new Vector3();
  const s = new Vector3();
  const axis = new Vector3();
  const c = new Color();
  // Prime instance colors so setColorAt allocates once.
  mesh.setColorAt(0, c.set('#FFFFFF'));

  function kill(i: number): void {
    alive--;
    if (i === alive) return;
    px.copyWithin(i * 3, alive * 3, alive * 3 + 3);
    pv.copyWithin(i * 3, alive * 3, alive * 3 + 3);
    spin.copyWithin(i * 3, alive * 3, alive * 3 + 3);
    life[i] = life[alive];
    maxLife[i] = maxLife[alive];
    size[i] = size[alive];
    stretch[i] = stretch[alive];
    grav[i] = grav[alive];
    colors[i].copy(colors[alive]);
  }

  return {
    mesh,
    burst(b) {
      const list = Array.isArray(b.color) ? b.color : [b.color];
      for (let k = 0; k < b.count; k++) {
        if (alive >= cap) kill(0);
        const i = alive++;
        const a = Math.random() * Math.PI * 2;
        const r = (b.spread ?? 0) * Math.sqrt(Math.random());
        px[i * 3] = b.x + Math.cos(a) * r;
        px[i * 3 + 1] = b.y;
        px[i * 3 + 2] = b.z + Math.sin(a) * r;
        const sp = b.speed * (0.4 + Math.random() * 0.8);
        const da = Math.random() * Math.PI * 2;
        pv[i * 3] = Math.cos(da) * sp;
        pv[i * 3 + 1] = b.up * (0.6 + Math.random() * 0.7);
        pv[i * 3 + 2] = Math.sin(da) * sp;
        spin[i * 3] = (Math.random() - 0.5) * 14;
        spin[i * 3 + 1] = (Math.random() - 0.5) * 14;
        spin[i * 3 + 2] = Math.random() * 6.28;
        life[i] = 0;
        maxLife[i] = b.life * (0.75 + Math.random() * 0.5);
        size[i] = b.size * (0.6 + Math.random() * 0.7);
        stretch[i] = b.stretch ?? 1;
        grav[i] = b.gravity ?? 14;
        colors[i].set(list[Math.floor(Math.random() * list.length)] as ColorRepresentation);
      }
    },
    setFloor(fn) {
      floor = fn;
    },
    update(dt) {
      for (let i = 0; i < alive; i++) {
        life[i] += dt;
        if (life[i] >= maxLife[i] || px[i * 3 + 1] < -3.4) {
          kill(i);
          i--;
          continue;
        }
        pv[i * 3 + 1] -= grav[i] * dt;
        const drag = Math.max(0, 1 - 0.6 * dt);
        pv[i * 3] *= drag;
        pv[i * 3 + 2] *= drag;
        px[i * 3] += pv[i * 3] * dt;
        px[i * 3 + 1] += pv[i * 3 + 1] * dt;
        px[i * 3 + 2] += pv[i * 3 + 2] * dt;
        const f = pv[i * 3 + 1] < 0 ? floor(px[i * 3], px[i * 3 + 2]) : null;
        if (f !== null && px[i * 3 + 1] < f + size[i] * 0.5 && px[i * 3 + 1] > f - 0.3) {
          px[i * 3 + 1] = f + size[i] * 0.5;
          pv[i * 3 + 1] *= -0.3;
          pv[i * 3] *= 0.6;
          pv[i * 3 + 2] *= 0.6;
          spin[i * 3] *= 0.5;
        }
      }
      for (let i = 0; i < alive; i++) {
        const t = life[i];
        const k = Math.min(1, (maxLife[i] - t) / 0.25) * Math.min(1, t / 0.05);
        axis.set(spin[i * 3], spin[i * 3 + 1], 1).normalize();
        q.setFromAxisAngle(axis, spin[i * 3 + 2] + t * Math.hypot(spin[i * 3], spin[i * 3 + 1]));
        const sz = size[i] * k;
        s.set(sz, sz * stretch[i], sz);
        m.compose(v.set(px[i * 3], px[i * 3 + 1], px[i * 3 + 2]), q, s);
        mesh.setMatrixAt(i, m);
        mesh.setColorAt(i, colors[i]);
      }
      mesh.count = alive;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    },
    dispose() {
      geo.dispose();
    },
  };
}
